import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Megaphone, Plus, X, Save, Eye } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { Json } from '@/integrations/supabase/types';

interface BannerSettings {
  enabled: boolean;
  badge_text: string;
  headline: string;
  subheading: string;
  price_label: string;
  price: string;
  value_props: string[];
  cta_text: string;
  cta_link: string;
}

const defaultSettings: BannerSettings = {
  enabled: true,
  badge_text: 'New Package',
  headline: 'Cyber Essentials for Micro Businesses',
  subheading: 'UK Government-backed certification for 1-9 employees',
  price_label: 'Starting from',
  price: '£495',
  value_props: ['Win contracts', 'Pass due diligence faster', '1-2 week delivery'],
  cta_text: 'Get Certified',
  cta_link: '/services/cyber-essentials-micro',
};

const HomepageBannerManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<BannerSettings>(defaultSettings);
  const [newValueProp, setNewValueProp] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'homepage_banner')
        .maybeSingle();

      if (error) throw error;

      if (data?.setting_value) {
        setSettings(data.setting_value as unknown as BannerSettings);
      }
    } catch (error: any) {
      console.error('Error loading banner settings:', error);
      toast({
        title: 'Error loading settings',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // First check if the setting exists
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'homepage_banner')
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('site_settings')
          .update({
            setting_value: settings as unknown as Json,
            updated_at: new Date().toISOString(),
          })
          .eq('setting_key', 'homepage_banner');

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('site_settings')
          .insert([{
            setting_key: 'homepage_banner',
            setting_value: settings as unknown as Json,
          }]);

        if (error) throw error;
      }

      toast({
        title: 'Settings saved',
        description: 'Homepage banner settings have been updated.',
      });
    } catch (error: any) {
      toast({
        title: 'Error saving settings',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const addValueProp = () => {
    if (newValueProp.trim() && settings.value_props.length < 5) {
      setSettings({
        ...settings,
        value_props: [...settings.value_props, newValueProp.trim()],
      });
      setNewValueProp('');
    }
  };

  const removeValueProp = (index: number) => {
    setSettings({
      ...settings,
      value_props: settings.value_props.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              Homepage Promotional Banner
            </CardTitle>
            <CardDescription>
              Configure the promotional banner displayed on the homepage
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="banner-enabled" className="text-sm">
              {settings.enabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id="banner-enabled"
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Badge and Headline */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="badge-text">Badge Text</Label>
            <Input
              id="badge-text"
              value={settings.badge_text}
              onChange={(e) => setSettings({ ...settings, badge_text: e.target.value })}
              placeholder="e.g., New Package, Limited Offer"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="headline">Headline</Label>
            <Input
              id="headline"
              value={settings.headline}
              onChange={(e) => setSettings({ ...settings, headline: e.target.value })}
              placeholder="Main banner headline"
            />
          </div>
        </div>

        {/* Subheading */}
        <div className="space-y-2">
          <Label htmlFor="subheading">Subheading</Label>
          <Input
            id="subheading"
            value={settings.subheading}
            onChange={(e) => setSettings({ ...settings, subheading: e.target.value })}
            placeholder="Secondary description text"
          />
        </div>

        {/* Price */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="price-label">Price Label</Label>
            <Input
              id="price-label"
              value={settings.price_label}
              onChange={(e) => setSettings({ ...settings, price_label: e.target.value })}
              placeholder="e.g., Starting from, Only"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="price">Price</Label>
            <Input
              id="price"
              value={settings.price}
              onChange={(e) => setSettings({ ...settings, price: e.target.value })}
              placeholder="e.g., £495, £995/month"
            />
          </div>
        </div>

        {/* Value Propositions */}
        <div className="space-y-2">
          <Label>Value Propositions (max 5)</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {settings.value_props.map((prop, index) => (
              <div
                key={index}
                className="flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
              >
                <span>{prop}</span>
                <button
                  type="button"
                  onClick={() => removeValueProp(index)}
                  className="hover:text-destructive transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {settings.value_props.length < 5 && (
            <div className="flex gap-2">
              <Input
                value={newValueProp}
                onChange={(e) => setNewValueProp(e.target.value)}
                placeholder="Add a value proposition"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addValueProp())}
              />
              <Button type="button" variant="outline" onClick={addValueProp}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="cta-text">Button Text</Label>
            <Input
              id="cta-text"
              value={settings.cta_text}
              onChange={(e) => setSettings({ ...settings, cta_text: e.target.value })}
              placeholder="e.g., Get Certified, Learn More"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cta-link">Button Link</Label>
            <Input
              id="cta-link"
              value={settings.cta_link}
              onChange={(e) => setSettings({ ...settings, cta_link: e.target.value })}
              placeholder="e.g., /services/cyber-essentials-micro"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Preview
          </Label>
          <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary via-primary/95 to-primary/90 p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                {settings.badge_text && (
                  <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent text-accent-foreground rounded-full mb-2">
                    {settings.badge_text}
                  </span>
                )}
                <h3 className="text-lg font-bold text-white">{settings.headline}</h3>
                <p className="text-white/80 text-sm">{settings.subheading}</p>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {settings.value_props.map((prop, i) => (
                  <span key={i} className="px-2 py-1 bg-white/10 rounded-full text-white text-xs">
                    {prop}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <p className="text-white/70 text-xs">{settings.price_label}</p>
                  <p className="text-xl font-bold text-white">{settings.price}</p>
                </div>
                <Button size="sm" className="bg-white text-primary hover:bg-white/90">
                  {settings.cta_text}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default HomepageBannerManagement;
